import { Component, Input, OnChanges } from '@angular/core';

type DiffLineType = 'add' | 'del' | 'ctx';

interface DiffLine {
  type: DiffLineType;
  text: string;
}

/**
 * Vista di sola lettura che mostra le differenze tra due oggetti JSON in stile "git diff"
 * unificato (una sola colonna): le righe rimosse dal blocco "prima" e quelle aggiunte nel
 * blocco "dopo" appaiono in sequenza, evidenziate a colori.
 *
 * Volutamente NON usa nessuna libreria di diff né il tree-editor `jsoneditor`: i blocchi
 * CAN Map hanno una forma nota e sono piccoli (decine di righe), quindi un confronto
 * riga-per-riga su JSON "pretty-printed", con un LCS scritto a mano, è più robusto,
 * prevedibile e leggibile di qualunque componente generico di terze parti.
 *
 * Se `before` è null/undefined (primo salvataggio: non c'è nulla da confrontare) mostra
 * semplicemente `after` in chiaro, senza colori fuorvianti.
 */
@Component({
  selector: 'app-canmap-json-diff',
  templateUrl: './canmap-json-diff.component.html',
  styleUrls: ['./canmap-json-diff.component.scss'],
})
export class CanmapJsonDiffComponent implements OnChanges {
  /** Blocco "prima" (attuale nella CAN Map). null/undefined => nessun confronto. */
  @Input() before: any = null;
  /** Blocco "dopo" (proposto). */
  @Input() after: any = null;

  /**
   * Righe calcolate una sola volta per ogni cambio di input (mai in un getter): il diff
   * LCS non deve essere ricalcolato ad ogni ciclo di change detection. I riferimenti a
   * `before`/`after` passati dal parent sono stabili (assegnati una sola volta), quindi
   * ngOnChanges scatta una sola volta.
   */
  lines: DiffLine[] = [];
  /** false quando non c'è un blocco "prima": la vista è una semplice anteprima, non un confronto */
  hasBefore = false;

  ngOnChanges(): void {
    const afterLines = this.toLines(this.after);
    if (this.before === null || this.before === undefined) {
      this.hasBefore = false;
      this.lines = afterLines.map(text => ({ type: 'ctx', text }));
      return;
    }
    this.hasBefore = true;
    this.lines = this.diffLines(this.toLines(this.before), afterLines);
  }

  private toLines(value: any): string[] {
    if (value === null || value === undefined) {
      return [];
    }
    try {
      return JSON.stringify(value, null, 2).split('\n');
    } catch {
      return [];
    }
  }

  /**
   * Diff riga-per-riga basato su LCS (Longest Common Subsequence): la stessa logica di un
   * "git diff" unificato. O(n*m) in tempo e memoria, irrilevante per blocchi di questa
   * dimensione.
   */
  private diffLines(a: string[], b: string[]): DiffLine[] {
    const n = a.length;
    const m = b.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }

    const out: DiffLine[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (a[i] === b[j]) {
        out.push({ type: 'ctx', text: a[i] });
        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        out.push({ type: 'del', text: a[i] });
        i++;
      } else {
        out.push({ type: 'add', text: b[j] });
        j++;
      }
    }
    while (i < n) {
      out.push({ type: 'del', text: a[i++] });
    }
    while (j < m) {
      out.push({ type: 'add', text: b[j++] });
    }
    return out;
  }
}
